import './initializations/dependencies';
import './index.css';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { unregister as unregisterServiceWorker } from './registerServiceWorker';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import ResponsiveIndicator from './components/ResponsiveIndicator';
import { ErrorBoundaryCreator, SensitiveDataRedactor } from '@soluto-home-web/platform-utils';
import { BreakPoints, SomethingWentWrongPage, partnerTheme } from '@soluto-home-web/platform-common-ui';
import { TweekProvider, withTweekKeys } from 'react-tweek';
import { getTweekRepository } from '@soluto-home-web/platform-core';
import { Helmet } from 'react-helmet';
import { logger, BugsnagLoggerOptions } from '@soluto-home-web/platform-utils';
import Report from 'bugsnag-js/types/report';
import './globals';

type AppType = React.ComponentClass<any> | React.StatelessComponent<any>;

const GlobalStyles = createGlobalStyle`
  html {
    font-size:18px;
  }

  @media ${BreakPoints.tablet} {
    html {
      font-size: 20px;
    }
  }
`;

const PageLoadedIndicator = styled.div`
  display: none;
`;

const CustomHelmet: React.SFC<{ metaDescription: string }> = ({ metaDescription }) => (
  <Helmet>
    <meta name="theme-color" content="#000000" />
    <meta name="description" content={metaDescription} />
    <meta content="IsAlive" />
    <link id="favicon" rel="icon" href={require('./fav-icon.svg')} />
  </Helmet>
);

const EnhancedHelmet = withTweekKeys({ metaDescription: 'soluto_home_web/build/meta_description' })(CustomHelmet);

const WithProviders = (Comp: AppType) => (props) => (
  <ThemeProvider theme={partnerTheme}>
    <TweekProvider value={getTweekRepository()}>
      <>
        <EnhancedHelmet />
        <Comp {...props} />
        <PageLoadedIndicator className="page-loaded-indicator" />
        {globals.NODE_ENV === 'development' && <ResponsiveIndicator />}
      </>
    </TweekProvider>
  </ThemeProvider>
);

const ErrorBoundaryFallBackComponent = () => (
  <ThemeProvider theme={partnerTheme}>
    <SomethingWentWrongPage />
  </ThemeProvider>
);

const beforeSendUnhandledError = (report: Report) => {
  SensitiveDataRedactor(report);
  const reportError = { ...report, name: report.errorClass, message: report.errorMessage };
  const loggerOptions: BugsnagLoggerOptions = { skipBugsnagReport: true };
  logger.error(report.errorMessage, reportError, {}, loggerOptions);
};

const ErrorBoundary = ErrorBoundaryCreator();
const WithErrorBoundary = (Comp: AppType) => (props) => (
  <ErrorBoundary FallbackComponent={ErrorBoundaryFallBackComponent} beforeSend={beforeSendUnhandledError}>
    <Comp {...props} />
  </ErrorBoundary>
);

export default function RenderApp(App: AppType): void {
  let CompositeApp = WithProviders(App);
  if (globals.NODE_ENV === 'production') CompositeApp = WithErrorBoundary(CompositeApp);

  ReactDOM.render(
    <>
      <GlobalStyles />
      <CompositeApp />
    </>,
    document.getElementById('root') as HTMLElement
  );
}

unregisterServiceWorker();



// WEBPACK FOOTER //
// ./packages/web-app/src/RenderApp.tsx